import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Table, Row, Col, Spin, message, Button } from "antd";
import styles from "@aspen/theme/History.module.less";
import {
  transformTime,
  decimalPointNoMoreX,
  convertUSD2USDC,
  getQueryValue,
  numberToThousands,
  i18nUtil,
  formatTableSelectOptions,
  parseDateRangeToTimestamps,
} from "@aspen/libs";
import { WithEmptyContent, TableDateFilter, TableFilterIcon, TableSelectFilter } from "@aspen/ui";
import { BUSINESS_TYPE } from "@adgm/cross-models";
import { ModalExportHistory, ExportStatus, DateRange } from "@aspen/widgets";
import { IExportHistoryCategory } from "@aspen/model";
import { accountHistory, exportHistory, exportHistoryPreview } from "@aspen/services";

const AccountHistory = () => {
  const intl = i18nUtil.t();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [accountHistoryTableFilters, setAccountHistoryTableFilters] = useState<{
    dateRange: [string, string] | null;
    orderType: string;
  }>({
    dateRange: null,
    orderType: "",
  });
  const [exportModalVisible, setExportModalVisible] = useState(false);

  const router = useRouter();
  const currency = router.query?.currency?.toString() ?? getQueryValue("currency");

  const loadMore = intl["button.loadMore"];

  const getAccountHistory = (reset: boolean = false) => {
    const { startTimestamp, endTimestamp } =
      parseDateRangeToTimestamps(accountHistoryTableFilters.dateRange) || {};
    setLoading(true);
    const params = {
      currency,
      offset: reset ? 0 : offset,
      limit: 10,
      startCreated: startTimestamp,
      endCreated: endTimestamp,
      businessType: accountHistoryTableFilters.orderType,
    };
    accountHistory(params)
      .then(res => {
        if (res?.code == "0") {
          const { rows = [], offset: newOffset } = res?.data || {};
          if (reset) {
            setDataSource(rows);
          } else {
            setDataSource(dataSource.concat(rows));
          }
          setShowLoadMore(rows.length >= 10);
          setOffset(newOffset);
        }
      })
      .catch(() => {
        message.error(intl["system.error"]);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useEffect(() => {
    getAccountHistory(true);
  }, [accountHistoryTableFilters]);

  const columns = [
    {
      title: (
        <div className="whitespace-nowrap overflow-hidden text-ellipsis w-full">
          {accountHistoryTableFilters.dateRange &&
          accountHistoryTableFilters.dateRange[0] &&
          accountHistoryTableFilters.dateRange[1]
            ? `${accountHistoryTableFilters.dateRange[0]}～${accountHistoryTableFilters.dateRange[1]}`
            : intl["column.date"]}
        </div>
      ),
      width: 150,
      dataIndex: "created",
      render: (value: number) => <span>{transformTime(value)}</span>,
      filterDropdown: ({ close }) => (
        <TableDateFilter
          value={accountHistoryTableFilters.dateRange}
          onChange={val => {
            setAccountHistoryTableFilters(prev => ({ ...prev, dateRange: val }));
            close();
          }}
        />
      ),
      filteredValue: accountHistoryTableFilters.dateRange ?? [],
      filterIcon: (filtered: boolean) => <TableFilterIcon filtered={filtered} />,
    },
    // 占位空列，用于表头布局对齐自定义筛选/排序按钮，避免错位，提升交互体验
    {
      title: "",
      dataIndex: "",
      width: 300,
    },
    {
      title:
        (accountHistoryTableFilters.orderType &&
          formatTableSelectOptions(BUSINESS_TYPE)?.find(
            opt => opt?.value === accountHistoryTableFilters.orderType,
          )?.label) ||
        intl["column.type"],
      dataIndex: "businessType",
      render: type => (
        <div style={{ textTransform: "capitalize" }}>
          {intl[BUSINESS_TYPE[type]] || BUSINESS_TYPE[type]}
        </div>
      ),
      width: 150,
      filterDropdown: ({ close }) => (
        <TableSelectFilter
          value={accountHistoryTableFilters.orderType}
          options={formatTableSelectOptions(BUSINESS_TYPE)}
          changeOption={val => {
            setAccountHistoryTableFilters(prev => ({ ...prev, orderType: val }));
            close();
          }}
          searchable
          containerClassName="w-[220px]"
        />
      ),
      filteredValue: accountHistoryTableFilters.orderType
        ? [accountHistoryTableFilters.orderType]
        : [],
      filterIcon: (filtered: boolean) => <TableFilterIcon filtered={filtered} />,
    },
    // 占位空列，用于表头布局对齐自定义筛选/排序按钮，避免错位，提升交互体验
    {
      title: "",
      dataIndex: "",
      width: 300,
    },
    {
      title: intl["column.total"],
      dataIndex: "coldMoney",
      render: (coldMoney, { operateType }) => {
        let symbol = "";
        if (operateType === 4 || operateType === 6) {
          symbol = "+";
        } else if (operateType === 5 || operateType === 3) {
          symbol = "-";
        }
        return (
          <div style={{ textTransform: "capitalize" }}>
            {symbol}
            {numberToThousands(decimalPointNoMoreX(coldMoney))}
          </div>
        );
      },
    },
  ];

  return (
    <section className={styles.history + " " + "customer"}>
      <div className="customerTop">
        <Row className="top">
          <Col span={24}>
            <div className={styles.titleContent}>
              <p className="title">
                {`${intl["history.spot.account.title"]}-${convertUSD2USDC(currency?.toString())}`}
              </p>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => {
                    setExportModalVisible(true);
                  }}
                  className={styles.exportBtn}>
                  {intl["export.history.title"]}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
      <div className={styles.tableWrap}>
        <div className="section" style={{ width: "100%", marginTop: 0 }}>
          <div className={styles.history} style={{ marginTop: 0 }}>
            <Spin spinning={loading}>
              <div className={styles.historyArea}>
                <WithEmptyContent showEmpty={true}>
                  <Table
                    className={styles.table}
                    bordered={false}
                    dataSource={dataSource}
                    columns={columns}
                    pagination={false}
                  />
                </WithEmptyContent>

                {showLoadMore && (
                  <div className={styles.loadMore} onClick={() => getAccountHistory(false)}>
                    {loadMore}
                  </div>
                )}
              </div>
            </Spin>
          </div>
        </div>
      </div>
      {exportModalVisible ? (
        <ModalExportHistory
          open={exportModalVisible}
          onClose={() => {
            setExportModalVisible(false);
          }}
          onExport={async (dateRange: DateRange): Promise<ExportStatus> => {
            if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
              return ExportStatus.INIT;
            }
            try {
              const startTime = new Date(dateRange.startDate).getTime();
              const endTime = new Date(dateRange.endDate).getTime();
              const exportParams = {
                category: IExportHistoryCategory.SPOT_ACCOUNT_HISTORY,
                startTime,
                endTime,
              };
              // 1. 调用当前选中的时间范围内是否有历史记录
              const previewResult = await exportHistoryPreview(exportParams);
              // 2. 如果没有，则直接返回 ExportStatus.NO_RECORDS
              if (!previewResult?.data) {
                return ExportStatus.NO_RECORDS;
              }
              // 3. 如果有，则调用导出接口，接口调用成功后，返回 ExportStatus.SUCCESS
              const exportResult = await exportHistory(exportParams);
              if (exportResult) {
                return ExportStatus.EXPORT_SUCCESS;
              } else {
                message.error(intl["system.error"]);
                return ExportStatus.INIT;
              }
            } catch (error) {
              message.error(intl["system.error"]);
              return ExportStatus.INIT;
            }
          }}
          descriptions={[
            { text: intl["export.account.history.description"][0] },
            { text: intl["export.account.history.description"][1], className: "text-[#f17e00]" },
            { text: intl["export.account.history.description"][2] },
          ]}
        />
      ) : null}
    </section>
  );
};

export const PageAccountHistory = AccountHistory;
